import { IReceipt, ReceiptRow } from '@interfaces/models/receipt.interface';
import {
  CountryUS,
  DecedentInfoState,
  IRATrustTypesMapper,
  ISelectedClientAccountsState,
  TransactionTypesEnum,
  urlMapper,
} from '@shared/models';
import { ReceiptModal } from '@shared/models/receipt-modal';
import { TrustNumber } from '@shared/models/trust';
import { ReviewContent } from '@shared/services/content/content-jsons/review-content';
import { IDocumentUploadState } from '@store/reducers/document-upload/document-upload.reducer';
import { EstateState } from '@store/reducers/estate/estate.reducer';
import {
  getTCFTrusteeInformation,
  TCFTrustState,
} from '@store/reducers/tcf-trust/tcf-trust.reducer';
import {
  getIsDecedentMarried,
  getIsInheritanceFlow,
  getIsIRAExecutor,
  getIsIRASingleExecutor,
  getIsIRATrust,
  getIsMultiInheritanceFlow,
  getIsSingleExecutor,
  getIsTrustToTrustFlow,
  TriageState,
} from '@store/reducers/triage/triage.reducer';
import { TrustState } from '@store/reducers/trust/trust.reducer';
import { TrustTaxInfoState } from '@store/reducers/trust-tax-info/trust-tax-info.reducer';
import * as dayjs from 'dayjs';

import { joinWithSpace } from '../cashier-nonretirement';
import { safeRowBuilder, splitClientId } from '../new-account/new-account.cashier';

export function buildDocumentUploadReceipt(
  state: IDocumentUploadState,
  editable: boolean = true,
): IReceipt {
  if (state.filesMetaData.length > 0) {
    const fileNameValues = state.filesMetaData.map((fileMetaData) => fileMetaData.fileName);

    const receipt: IReceipt = {
      header: 'Legal documents',
      description: null,
      editable: editable,
      editRoute: 'secure/inheritance/upload',
      sectionEvidenceID: 'documentUpload',
      rows: [
        {
          label: 'Documents uploaded',
          values: fileNameValues,
          evidenceID: 'docName',
        },
      ],
    };
    return receipt;
  } else {
    return null;
  }
}

export function buildDecedentInfoReceipt(
  state: DecedentInfoState,
  triage: TriageState,
  editable: boolean = true,
): IReceipt {
  const isNotInheritanceFlow = !getIsInheritanceFlow(triage) && !getIsMultiInheritanceFlow(triage);

  if (isNotInheritanceFlow) return null;

  const isIRAExecutor = getIsIRASingleExecutor(triage);
  const isDecedentMarried = getIsDecedentMarried(triage);
  const fullName = joinWithSpace([state.firstName, state.middleName, state.lastName]);

  const receipt: IReceipt = {
    header: 'Decedent information',
    description: null,
    editable: editable,
    editRoute: 'secure/inheritance/decedent-info',
    sectionEvidenceID: 'accountOwner',
    rows: [
      {
        label: 'Name',
        values: [fullName],
        evidenceID: 'accountOwnerName',
      },
      {
        label: 'Date of birth',
        values: [state.dateOfBirth],
        evidenceID: 'accountOwnerDob',
      },
    ],
  };

  if (editable) {
    safeRowBuilder(
      receipt.rows,
      'Social Security number',
      state.decedentId_private,
      'accountOwnerClientId-private',
    );
  } else {
    safeRowBuilder(
      receipt.rows,
      'Social Security number',
      '***-**-' + splitClientId(state.decedentId_private),
      'accountOwnerClientId-private',
    );
  }

  receipt.rows.push(
    {
      label: 'Zip code',
      values: [state.zipCode],
      evidenceID: 'accountOwnerZipcode',
    },
    {
      label: 'Date of death',
      values: [state.dateOfDeath],
      evidenceID: 'accountOwnerDod',
    },
  );

  if (isIRAExecutor) {
    receipt.rows.push({
      label: 'Marital status at the time of passing',
      values: [isDecedentMarried ? 'Married' : 'Not married'],
      evidenceID: 'decedentsMaritalStatus',
    });
  }

  return receipt;
}

export function buildEstateReceipt(
  estateState: EstateState,
  triageState: TriageState,
  editable: boolean,
): IReceipt {
  const isNotIRAExecutor = !getIsIRAExecutor(triageState);
  const isNotSingleExecutor = !getIsSingleExecutor(triageState);
  if (isNotIRAExecutor || isNotSingleExecutor) return null;

  const rows: ReceiptRow[] = [
    {
      label: 'Estate name',
      values: [estateState.estateName],
      evidenceID: 'estateName',
    },
  ];

  if (estateState.tin_private) {
    const tin = editable
      ? estateState.tin_private
      : '**-***' + splitClientId(estateState.tin_private);
    rows.push({
      label: 'Estate tax identification number',
      values: [tin],
      evidenceID: 'estatesIdentificationNumber',
    });
  }

  rows.push({
    label: 'Federal tax classification',
    values: ['Estate'],
    evidenceID: 'estateFederalClassIdentification',
  });

  if (estateState.countryQuestion) {
    rows.push({
      label: 'Country of establishment',
      values: [estateState.countryQuestion],
      evidenceID: 'estateCountry',
    });
  }

  if (estateState.taxResidencyQuestion) {
    rows.push({
      label: 'Tax residency',
      values: [estateState.taxResidencyQuestion],
      evidenceID: 'estateTaxResidency',
    });
  }

  if (estateState.withholdingQuestion) {
    rows.push({
      label: 'Subject to backup withholding',
      values: [estateState.withholdingQuestion],
      evidenceID: 'subjectToBackupWithHolding',
    });
  }

  const flow = urlMapper[triageState.flowType];

  const receipt: IReceipt = {
    header: 'Estate tax information',
    description: null,
    editable: editable,
    editRoute: `secure/${flow}/estate`,
    sectionEvidenceID: 'estate',
    rows: rows,
  };

  return receipt;
}

export function buildFundingReceipt(
  triageState: TriageState,
  selectedClientAccountsState: ISelectedClientAccountsState,
  editable: boolean,
): IReceipt {
  const isNotIRAExecutor = !getIsIRAExecutor(triageState);
  const isNotSingleExecutor = !getIsSingleExecutor(triageState);
  const isNotIRAtoEstate = isNotIRAExecutor || isNotSingleExecutor;
  const isTransactionTypeExisting =
    TransactionTypesEnum.EXISTING === selectedClientAccountsState.transactionType;
  const isNotTrustToTrustFlow = !getIsTrustToTrustFlow(triageState);

  if (isTransactionTypeExisting || (isNotIRAtoEstate && isNotTrustToTrustFlow)) return null;

  const flow = urlMapper[triageState.flowType];
  const receipt: IReceipt = {
    header: 'Funding',
    description: null,
    editable: editable,
    editRoute: `secure/${flow}/funding`,
    sectionEvidenceID: 'funding',
    rows: [
      {
        label: 'Funding method',
        values: ['Asset transfer'],
        evidenceID: 'fundingMethod',
      },
      {
        label: 'Dividends & capital gains',
        values: ['Reinvest distributions'],
        modal: ReceiptModal.DIVCAPGAINS,
        evidenceID: 'divAndCap',
      },
      {
        label: 'Settlement fund',
        values: ['Vanguard Federal Money Market Fund'],
        modal: ReceiptModal.SETTLEMENTFUND,
        evidenceID: 'settlementFund',
        additionalText: ReviewContent.fundInvestWarning,
      },
    ],
  };

  return receipt;
}

export function buildTrustInfoReceipt(
  triageState: TriageState,
  trustInfoState: TrustState,
  trustTaxInfoState: TrustTaxInfoState,
  tcfTrustState: TCFTrustState,
  editable: boolean,
): IReceipt {
  const isIRAToTrust = getIsIRATrust(triageState);
  const isTrustToTrustFLow = getIsTrustToTrustFlow(triageState);

  if (!isIRAToTrust && !isTrustToTrustFLow) return null;

  const rows = [];

  if (isIRAToTrust) {
    rows.push(
      {
        label: 'Trust type',
        values: [IRATrustTypesMapper[triageState.typeOfTrust]],
        evidenceID: 'trustType',
      },
      {
        label: 'Trust name',
        values: [trustInfoState.trustName],
        evidenceID: 'trustName',
      },
    );

    if (trustInfoState.trustDate) {
      rows.push({
        label: 'Trust date',
        values: [trustInfoState.trustDate],
        evidenceID: 'trustDate',
      });
    }

    rows.push({
      label: 'Federal tax classification',
      values: [trustInfoState.numberQuestion === TrustNumber.EIN ? 'Trust' : 'Individual'],
      evidenceID: 'trustTaxClassification',
    });

    if (trustInfoState.numberQuestion === TrustNumber.EIN) {
      rows.push({
        label: 'Trust tax identification number',
        values: editable
          ? [trustTaxInfoState.ein + ' (EIN)']
          : ['**-***' + splitClientId(trustTaxInfoState.ein) + ' (EIN)'],
        evidenceID: 'trustTaxIdentificationNumber',
      });
    }
  }

  if (isTrustToTrustFLow) {
    const trustName = trustInfoState.trustName
      ? trustInfoState.trustName
      : tcfTrustState.info.trustName;
    const trustDate = trustInfoState.trustDate
      ? trustInfoState.trustDate
      : tcfTrustState.info.trustDate;

    rows.push(
      {
        label: 'Trust name',
        values: [trustName],
        evidenceID: 'trustName',
      },
      {
        label: 'Trust date',
        values: [dayjs(trustDate).format('MM/DD/YYYY')],
        evidenceID: 'trustDate',
      },
      {
        label: 'Federal tax classification',
        values: [trustTaxInfoState.numberQuestion === TrustNumber.EIN ? 'Trust' : 'Individual'],
        evidenceID: 'trustTaxClassification',
      },
    );

    if (trustTaxInfoState.numberQuestion === TrustNumber.EIN) {
      rows.push({
        label: 'Trust tax identification number',
        values: editable
          ? [trustTaxInfoState.ein + ' (EIN)']
          : ['**-***' + splitClientId(trustTaxInfoState.ein) + ' (EIN)'],
        evidenceID: 'trustTaxIdentificationNumber',
      });
    }
  }

  const trustAddress = trustInfoState.trustAddress
    ? ['Same as trustee']
    : [
        trustInfoState.address,
        `${trustInfoState.city}, ${trustInfoState.state} ${trustInfoState.zipCode}`,
      ];

  const mailingAddress = trustInfoState.mailingAddress
    ? ['Same as trust']
    : [
        trustInfoState.mailAddress,
        `${trustInfoState.mailCity}, ${trustInfoState.mailState} ${trustInfoState.mailZipCode}`,
      ];

  rows.push(
    {
      label: 'Trust address',
      values: trustAddress,
      evidenceID: 'trustAddress',
    },
    {
      label: 'Trust mailing address',
      values: mailingAddress,
      evidenceID: 'trustMailingAddress',
    },
  );

  if (
    trustInfoState.numberQuestion === TrustNumber.SSN ||
    trustTaxInfoState.numberQuestion === TrustNumber.SSN
  ) {
    rows.push(
      {
        label: 'Grantor',
        values: [trustTaxInfoState.grantorName],
        evidenceID: 'trustGrantorName',
      },
      {
        label: 'Grantor Social Security number',
        values: editable
          ? [trustTaxInfoState.ssn]
          : ['***-**-' + splitClientId(trustTaxInfoState.ssn)],
        evidenceID: 'trustGrantorSSN',
      },
    );
  }

  if (trustInfoState.numberQuestion === TrustNumber.SSN) {
    rows.push({
      label: 'Grantor citizenship',
      values: [trustInfoState.grantorQuestion === CountryUS.US ? 'U.S. citizen' : 'Other'],
      evidenceID: 'trustGrantorName',
    });
  }

  rows.push(
    {
      label: 'Country of establishment',
      values: [trustInfoState.countryQuestion],
      evidenceID: 'establishmentCountry',
    },
    {
      label: 'Tax residency',
      values: [trustInfoState.taxResidencyQuestion],
      evidenceID: 'taxResidency',
    },
    {
      label: 'Subject to backup withholding',
      values: [trustTaxInfoState.withholdingQuestion],
      evidenceID: 'backupWithholding',
    },
  );

  const flow = urlMapper[triageState.flowType];

  const receipt: IReceipt = {
    header: 'Trust information',
    description: null,
    editable: editable,
    editRoute: `secure/${flow}/trust-info`,
    sectionEvidenceID: 'trustInfo',
    rows: rows,
  };

  return receipt;
}

export function buildTrusteeReceipt(
  triageState: TriageState,
  tcfTrustState: TCFTrustState,
  isRemovingTrustee: boolean,
  editable: boolean,
): IReceipt {
  const isTrustToTrustFlow = getIsTrustToTrustFlow(triageState);
  const trusteeInformation = getTCFTrusteeInformation(tcfTrustState);
  const trustee = trusteeInformation.find((trustee) => trustee.isRemove === isRemovingTrustee);
  const prefix = isRemovingTrustee ? 'removed' : 'added';

  if (!isTrustToTrustFlow || !trustee) return null;

  const rows = [
    {
      label: 'Name',
      values: [trustee.trusteeName],
      evidenceID: prefix + 'TrusteeName',
    },
    {
      label: 'Social Security number',
      values: editable ? [trustee.ssn] : ['***-**-' + splitClientId(trustee.ssn)],
      evidenceID: prefix + 'TrusteeSSN',
    },
  ];

  const flow = urlMapper[triageState.flowType];

  const receipt: IReceipt = {
    header: isRemovingTrustee ? 'Removed trustee' : 'New trustee',
    description: null,
    editable: editable,
    editRoute: `secure/${flow}/tcf-info-trust`,
    sectionEvidenceID: prefix + 'Trustee',
    rows: rows,
  };

  return receipt;
}

export function buildAccountFromTrust(tcfTrustState: TCFTrustState, editable: boolean) {
  const { trustName, trustDate, ssnOrEIN } = tcfTrustState.info;

  const receipt: IReceipt = {
    header: 'Transferring from',
    editable: false,
    editRoute: '',
    sectionEvidenceID: 'accountFrom',
    rows: [],
  };

  receipt.rows.push(
    {
      label: 'Account type',
      values: ['Trust brokerage account'],
      evidenceID: 'accountType',
    },
    {
      label: 'Source of Funds',
      values: ['Family/Inheritance'],
      evidenceID: 'sourceOfFunds',
    },
    {
      label: 'Trust name',
      values: [trustName],
      evidenceID: 'trustName',
    },
    {
      label: 'Trust date',
      values: [dayjs(trustDate).format('MM/DD/YYYY')],
      evidenceID: 'trustDate',
    },
    {
      label: 'SSN/EIN',
      values: editable ? [ssnOrEIN] : ['*****' + splitClientId(ssnOrEIN)],
      evidenceID: 'ssnOrEIN',
    },
  );
  return receipt;
}

export function buildAccountToTrust() {
  const receipt: IReceipt = {
    header: 'Transferring to',
    editable: false,
    editRoute: '',
    sectionEvidenceID: 'accountTo',
    rows: [],
  };

  receipt.rows.push({
    label: 'Account type',
    values: ['Trust brokerage account'],
    evidenceID: 'toAccountType',
  });

  return receipt;
}
